import { useState } from "react";
import { useDiscountCodes, type DiscountResult } from "@/hooks/useDiscountCodes";
import { useOrganization } from "@/hooks/useOrganization";
import { formatPrice } from "@/utils/formatPrice";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Tag, X } from "lucide-react";
import { useTranslation } from "react-i18next";

interface DiscountCodeInputProps {
  /** Total amount in kronor before discount */
  totalAmount: number;
  /** Activity type for applicability check, e.g. "court_booking" */
  applicableTo?: string;
  /** Called when a valid discount is applied or removed. Amount in kronor. */
  onDiscountChange: (discountAmount: number, discountCodeId?: string) => void;
}

export default function DiscountCodeInput({
  totalAmount,
  applicableTo,
  onDiscountChange,
}: DiscountCodeInputProps) {
  const { organization } = useOrganization();
  const { validateCode } = useDiscountCodes(organization?.id);
  const { t } = useTranslation("booking");

  const [expanded, setExpanded] = useState(false);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<DiscountResult | null>(null);

  const handleApply = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setError(null);

    try {
      const result = await validateCode(code, {
        totalAmount,
        applicableTo,
      });

      if (result.valid && result.discount) {
        setApplied(result.discount);
        setError(null);
        onDiscountChange(result.discount.discountAmount, result.discount.code.id);
      } else {
        setError(result.error ?? t("invalidCode"));
        setApplied(null);
        onDiscountChange(0);
      }
    } catch {
      setError(t("somethingWentWrong"));
      setApplied(null);
      onDiscountChange(0);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = () => {
    setApplied(null);
    setCode("");
    setError(null);
    onDiscountChange(0);
  };

  // If a discount is applied, show the badge
  if (applied) {
    return (
      <div className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
        <div className="flex items-center gap-2 text-sm text-emerald-700">
          <Tag className="h-4 w-4" />
          <span className="font-medium">{applied.code.code}</span>
          <span>
            &minus;{formatPrice(applied.discountAmount)}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 text-emerald-600 hover:text-emerald-800"
          onClick={handleRemove}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    );
  }

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <Tag className="h-3.5 w-3.5" />
        {t("discountCode")}
      </button>
    );
  }

  return (
    <div className="space-y-1.5">
      <div className="flex gap-2">
        <Input
          value={code}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase());
            setError(null);
          }}
          placeholder={t("enterCode")}
          className="font-mono uppercase"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleApply();
            }
          }}
          disabled={loading}
        />
        <Button
          onClick={handleApply}
          disabled={!code.trim() || loading}
          size="sm"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            t("apply")
          )}
        </Button>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
